import lodash from 'lodash';
import classnames from 'classnames';
import { IoMdClose } from 'react-icons/io';
import { UiSearch } from '@vyaguta/vyaguta-icons/ui';
import React, { useMemo, useState, useEffect } from 'react';

import * as roleService from 'services/role';

import { parse } from 'utils/queryParam';
import { pluralize } from 'utils/string';
import { handleError } from 'utils/errorHandler';

import { MAX_FILTER_TEXT_LENGTH } from 'constants/filters';

import DropDownSearch from 'components/common/dropdownsearch';

import type { QueryObject } from 'interface/queryParam';
import type { QueryValue } from './types';

type RoleApiItem = { id: number; name: string };

type DropdownOption = {
  value: string | number;
  label?: string;
};

type QueryParamRecord = Record<string, QueryValue>;
type FilterQuery = { q?: QueryValue; roleIds?: QueryValue };

const toStringValue = (value: QueryValue): string | undefined => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') return value[0];
  return undefined;
};

const toCommaSeparatedString = (value: QueryValue): string | undefined => {
  const asString = toStringValue(value);
  if (asString !== undefined) return asString;

  if (Array.isArray(value) && value.length > 0 && (typeof value[0] === 'string' || typeof value[0] === 'number')) {
    return value.map(String).join(',');
  }

  return undefined;
};

interface Props {
  data: unknown[];
  applyFilter: (filter: QueryObject) => void;
  setIsFilterApplied: (value: boolean) => void;
  isFilterApplied: boolean;
  handleAddUserClick: (open: boolean) => void;
  location: { search: string };
}

const UserPermissionsFilter: React.FC<Props> = (props) => {
  const { data, applyFilter, setIsFilterApplied, isFilterApplied, handleAddUserClick } = props;

  const parsed = parse(props.location.search) as QueryParamRecord;
  const queryParams = parsed as FilterQuery;

  const [filters, setFilters] = useState<QueryObject>(() => {
    const initialFilters: QueryObject = {};
    const q = toStringValue(queryParams.q);
    const roleIds = toCommaSeparatedString(queryParams.roleIds);
    if (q) initialFilters.q = q;
    if (roleIds) initialFilters.roleIds = roleIds;
    return initialFilters;
  });

  const [isFetchingRole, setIsFetchingRole] = useState(false);
  const [isFilterChanged, setIsFilterChanged] = useState(false);
  const [rolesData, setRolesData] = useState<RoleApiItem[]>([]);
  const [selectedRolesList, setSelectedRolesList] = useState<DropdownOption[]>([]);

  useEffect(() => {
    (async () => {
      try {
        setIsFetchingRole(true);
        const roles = (await roleService.fetchAll()) as RoleApiItem[];

        setRolesData(roles);

        const selectedRoleIdsCsv = toCommaSeparatedString(queryParams.roleIds);
        const selectedRoleIds = selectedRoleIdsCsv ? selectedRoleIdsCsv.split(',') : [];
        if (selectedRoleIds.length > 0) {
          const selectedOptions = selectedRoleIds
            .map((selectedId) => {
              const match = roles.find((item) => String(item.id) === selectedId);
              if (!match) {
                return undefined;
              }

              return {
                value: selectedId,
                label: match.name,
              };
            })
            .filter((item): item is NonNullable<typeof item> => item !== undefined);

          setSelectedRolesList(selectedOptions);
        }
      } catch (error) {
        handleError(error);
      } finally {
        setIsFetchingRole(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const roleOptions = useMemo<DropdownOption[]>(() => {
    const rolesList = rolesData.map(({ id, name }) => ({ value: id, label: name }));
    return lodash.sortBy(rolesList, ['label']);
  }, [rolesData]);

  const showEllipses = (value: DropdownOption): DropdownOption => {
    if (value.label && String(value.label).length > MAX_FILTER_TEXT_LENGTH) {
      return { ...value, label: String(value.label).slice(0, MAX_FILTER_TEXT_LENGTH) + '...' };
    }

    return value;
  };

  const updateFilter = (newState: QueryObject) => {
    setFilters((prevState) => ({ ...prevState, ...newState }));
    setIsFilterChanged(true);
  };

  const handleApplyFilter = () => {
    applyFilter(filters);
    setIsFilterChanged(false);
    setIsFilterApplied(true);
  };

  const handleSearchClose = () => {
    const updatedFilters = { ...filters, q: undefined };
    if (!toStringValue(queryParams.q)) {
      setFilters(updatedFilters);
      return;
    }

    setFilters(updatedFilters);
    applyFilter(updatedFilters);
  };

  const handleOnRoleChange = (options: DropdownOption[]) => {
    setSelectedRolesList(options);

    if (options.length === 0) {
      setFilters((prev) => ({ ...prev, roleIds: undefined }));
      return;
    }

    updateFilter({ roleIds: options.map(({ value }) => value) });
  };

  return (
    <div className="full-scope-card__header table-header name-wrap py-10">
      <div>
        <div className="table-title">Role Management</div>
        <div className="table-subtitle">
          Showing {data.length} {pluralize('Result', data.length)}
        </div>
      </div>

      <div className="d-flex align-items-center">
        <div className="d-flex flex-row align-items-center roles__search-bar">
          <UiSearch className="roles__search-icon" />
          <input
            className="roles__search-input"
            value={typeof filters.q === 'string' ? filters.q : ''}
            type="text"
            onKeyDown={(e) => (e.key === 'Enter' ? (isFilterChanged ? handleApplyFilter() : undefined) : undefined)}
            onChange={(event) => updateFilter({ q: event.target.value.trimLeft() })}
            placeholder="Search by Name"
          />
          {typeof filters.q === 'string' && filters.q.trim().length > 0 && (
            <IoMdClose className="roles__search-icon__close" onClick={handleSearchClose} />
          )}
        </div>

        <DropDownSearch
          showSelected={true}
          title="Role"
          values={selectedRolesList.map(showEllipses)}
          isLoading={isFetchingRole}
          options={roleOptions}
          components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
          className={classnames('lf-select', { 'lf-filter-applied': false }, 'py-0')}
          onChange={handleOnRoleChange}
          searchLabel="All"
        />

        <div className="title-bar__filter-buttons d-flex align-items-center">
          <button
            className={classnames('button m-10 ml-24', {
              'button--primary': !isFilterChanged,
              'button--secondary': isFilterChanged,
            })}
            onClick={isFilterChanged ? handleApplyFilter : undefined}
          >
            Apply Filter
          </button>

          <button
            className={classnames('button__reset', {
              'button__reset--active': isFilterApplied,
            })}
            onClick={
              isFilterApplied
                ? () => {
                    setFilters({});
                    setIsFilterApplied(false);
                    setSelectedRolesList([]);
                    applyFilter({});
                  }
                : undefined
            }
          >
            Reset
          </button>

          <button className="button m-10 ml-24 button--secondary" onClick={() => handleAddUserClick(true)}>
            <span className="d-flex align-items-center white-space-nowrap">Add User</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserPermissionsFilter;
